import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { downloadFileWithFallback } from 'helpers/downloadFile';
import { RootState } from 'app/store';
import {
    CatalogItem,
    CATALOG_CATEGORY_MAP,
    CATALOG_STATUS_MAP,
} from '../models';
import AgDataGridServerPaging from 'components/DataGrid/AgDataGridServerPaging';
import { rowNumberRenderer } from 'components/DataGrid/agGridCustomProps';
import { BiShow, BiDownload, BiCheckCircle, BiTrash } from 'react-icons/bi';
import { MdOutlineMoreVert } from 'react-icons/md';
import {
    RiFileTextLine,
    RiFileExcel2Line,
    RiFileWord2Line,
    RiFilePpt2Line,
    RiFilePdf2Line,
} from 'react-icons/ri';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
    approveCatalogStatus,
    fetchCatalogList,
    deleteCatalogAction,
} from '../catalog.reducer';
import { UserCredential } from 'features/login/models';

interface CatalogTableProps {
    onSelectDetail: (item: CatalogItem) => void;
    onPreview?: (item: CatalogItem) => void;
    onDownload?: (item: CatalogItem) => void;
    filter?: string;
    category?: number;
    status?: number;
    refreshTrigger?: number;
}

const CatalogTable: React.FC<CatalogTableProps> = ({
    onSelectDetail,
    onPreview,
    onDownload,
    filter,
    category,
    status,
    refreshTrigger = 0,
}) => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);
    const user = useAppSelector(
        (state: RootState) => state.user.userAuth as UserCredential,
    );
    const gridRef = React.useRef<any>(null);

    const filtersRef = React.useRef({ filter, category, status });

    React.useEffect(() => {
        filtersRef.current = { filter, category, status };
        if (gridRef.current?.api) {
            gridRef.current.api.refreshServerSide({ purge: true });
        }
    }, [filter, category, status]);

    React.useEffect(() => {
        if (refreshTrigger > 0 && gridRef.current?.api) {
            gridRef.current.api.refreshServerSide({ purge: true });
        }
    }, [refreshTrigger]);

    const getServerSideDataSource = React.useCallback(() => {
        return {
            getRows: (params: any) => {
                const currentFilters = filtersRef.current;
                dispatch(
                    fetchCatalogList({
                        skip: params.request.startRow,
                        take: params.api.paginationGetPageSize(),
                        filter: currentFilters.filter,
                        category: currentFilters.category,
                        status: currentFilters.status,
                    }),
                )
                    .then((res: any) => {
                        params.success({
                            rowData: res.payload?.data?.data || [],
                            rowCount: res.payload?.data?.total || 0,
                        });
                    })
                    .catch(() => {
                        params.fail();
                    });
            },
        };
    }, [dispatch]);

    const canApprove =
        user?.roleName?.some(
            (r) =>
                r.toUpperCase().includes('HEAD') ||
                r.toLowerCase() === 'admin' ||
                r.toLowerCase() === 'administrator' ||
                r.toLowerCase() === 'superadmin',
        ) || false;

    const handleApproveClicked = (item: CatalogItem) => {
        MySwal.fire({
            title: 'Approve & Publish Catalog?',
            text: `Are you sure to publish this catalog?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10B981',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, Publish!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(approveCatalogStatus(item.id)).then(() => {
                    if (gridRef.current?.api) {
                        gridRef.current.api.refreshServerSide({ purge: true });
                    }
                });
                MySwal.fire(
                    'Published!',
                    'The catalog document status has been updated to Published.',
                    'success',
                );
            }
        });
    };

    const handleDeleteClicked = (item: CatalogItem) => {
        MySwal.fire({
            title: 'Delete Catalog?',
            text: `Are you sure you want to delete this catalog?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, Delete!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCatalogAction(item.id)).then(() => {
                    if (gridRef.current?.api) {
                        gridRef.current.api.refreshServerSide({ purge: true });
                    }
                });
                MySwal.fire(
                    'Deleted!',
                    'The catalog document has been successfully deleted.',
                    'success',
                );
            }
        });
    };

    // Custom Cell Renderers
    const DocumentNameRenderer = (props: any) => {
        const data: CatalogItem = props.data;
        if (!data) return null;

        let IconComponent = RiFileTextLine;
        let iconBg =
            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
        let badgeText = 'FILE';

        const fileName =
            data.originalFileName || data.file?.split('/').pop() || 'document';
        const fileExt = fileName.split('.').pop()?.toLowerCase() || '';

        if (fileExt === 'pdf') {
            IconComponent = RiFilePdf2Line;
            iconBg =
                'bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-400';
            badgeText = 'PDF';
        } else if (['doc', 'docx'].includes(fileExt)) {
            IconComponent = RiFileWord2Line;
            iconBg =
                'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400';
            badgeText = 'DOCX';
        } else if (['xls', 'xlsx'].includes(fileExt)) {
            IconComponent = RiFileExcel2Line;
            iconBg =
                'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400';
            badgeText = 'XLSX';
        } else if (['ppt', 'pptx'].includes(fileExt)) {
            IconComponent = RiFilePpt2Line;
            iconBg =
                'bg-orange-50 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400';
            badgeText = 'PPTX';
        }

        return (
            <div
                className="flex items-center py-2 cursor-pointer group"
                onClick={() => onSelectDetail(data)}
            >
                <div
                    className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center font-bold text-[10px] shrink-0 mr-3 shadow-sm transition-transform group-hover:scale-105 ${iconBg}`}
                >
                    <IconComponent size={20} />
                    <span className="text-[9px] mt-0.5 leading-none font-extrabold">
                        {badgeText}
                    </span>
                </div>
                <div className="overflow-hidden">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white truncate group-hover:text-primary-color transition-colors">
                        {data.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {fileName}
                    </p>
                </div>
            </div>
        );
    };

    const CategoryRenderer = (props: any) => {
        const cat = CATALOG_CATEGORY_MAP[props.value as number] || 'SOP';
        let badgeStyle =
            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

        const lower = cat.toLowerCase();
        if (lower === 'sop')
            badgeStyle =
                'bg-[#4c8b6a]/10 text-[#4c8b6a] border border-[#4c8b6a]/30 dark:bg-[#4c8b6a]/20 dark:text-[#4c8b6a]';
        else if (lower.includes('work instruction') || lower === 'wi')
            badgeStyle =
                'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
        else if (lower === 'form' || lower.includes('checklist'))
            badgeStyle =
                'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800';
        else if (lower === 'policy')
            badgeStyle =
                'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
        else if (lower === 'training')
            badgeStyle =
                'bg-[#4c8b6a]/10 text-[#4c8b6a] border border-[#4c8b6a]/30 dark:bg-[#4c8b6a]/20 dark:text-[#4c8b6a]';
        else if (lower.includes('ocalc'))
            badgeStyle =
                'bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800';

        return (
            <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-block ${badgeStyle}`}
            >
                {cat}
            </span>
        );
    };

    const VersionRenderer = (props: any) => {
        const ver = props.value || 'V1.0';
        return (
            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 font-bold text-xs dark:bg-blue-950 dark:text-blue-400 border border-blue-200 dark:border-blue-900">
                {ver}
            </span>
        );
    };

    const StatusRenderer = (props: any) => {
        const status = CATALOG_STATUS_MAP[props.value as number] || 'Draft';
        let dotColor = 'bg-gray-400';
        let textColor = 'text-gray-700 dark:text-gray-300';
        let bgStyle =
            'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';

        if (status === 'Published') {
            dotColor = 'bg-emerald-500';
            textColor = 'text-emerald-700 dark:text-emerald-300 font-medium';
            bgStyle =
                'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900';
        } else if (status === 'Draft') {
            dotColor = 'bg-amber-500 animate-pulse';
            textColor = 'text-amber-700 dark:text-amber-300 font-medium';
            bgStyle =
                'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900';
        } else if (status === 'Archived') {
            dotColor = 'bg-gray-400';
            textColor = 'text-gray-600 dark:text-gray-400';
            bgStyle =
                'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600';
        }

        return (
            <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${bgStyle} ${textColor}`}
            >
                <span className={`w-2 h-2 rounded-full mr-1.5 ${dotColor}`} />
                {status}
            </span>
        );
    };

    const UploadedByRenderer = (props: any) => {
        const data: CatalogItem = props.data;
        if (!data) return null;

        const uploaderName =
            data.createby_id_name || data.createdby_name || 'System User';
        const initials = uploaderName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();

        return (
            <div className="flex items-center py-1">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#4c8b6a] to-[#3d7055] text-white font-bold text-xs flex items-center justify-center mr-2.5 shrink-0 shadow-sm">
                    {initials}
                </div>
                <div className="overflow-hidden">
                    <p className="text-xs font-semibold text-gray-800 dark:text-white truncate">
                        {uploaderName}
                    </p>
                    {/* <p className="text-[10px] text-gray-400 truncate">Author</p> */}
                </div>
            </div>
        );
    };

    const ActionCellRenderer = (props: any) => {
        const data: CatalogItem = props.data;
        if (!data) return null;

        const isPending = data.status === 0;

        return (
            <div className="flex items-center justify-center space-x-2 py-1">
                {/* Approve Button for Head role when status is Pending Review */}
                {isPending && canApprove && (
                    <button
                        onClick={() => handleApproveClicked(data)}
                        title="Approve and Publish"
                        className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:hover:bg-emerald-900 dark:text-emerald-400 rounded-lg transition-colors flex items-center shadow-sm"
                    >
                        <BiCheckCircle size={18} className="mr-1" />
                        <span className="text-xs font-bold">Approve</span>
                    </button>
                )}

                {/* Preview Button */}
                <button
                    onClick={() =>
                        onPreview ? onPreview(data) : onSelectDetail(data)
                    }
                    title="Preview Document"
                    className="p-1.5 text-gray-500 hover:text-[#4c8b6a] hover:bg-[#4c8b6a]/10 dark:hover:bg-[#4c8b6a]/20 dark:hover:text-[#4c8b6a] rounded-lg transition-colors"
                >
                    <BiShow size={18} />
                </button>

                {/* Download Button */}
                <button
                    onClick={() => {
                        if (onDownload) onDownload(data);
                        else if (data.file) {
                            const dlName = data.originalFileName || data.file.split('/').pop() || 'document';
                            downloadFileWithFallback(data.file, dlName);
                        }
                    }}
                    title="Download Document"
                    className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 dark:hover:text-emerald-400 rounded-lg transition-colors"
                >
                    <BiDownload size={18} />
                </button>

                {/* View Detail / More Options Button */}
                {/* <button
                    onClick={() => onSelectDetail(data)}
                    title="View Details & History"
                    className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-white rounded-lg transition-colors"
                >
                    <MdOutlineMoreVert size={18} />
                </button> */}

                {/* Delete Button */}
                {canApprove && (
                    <button
                        onClick={() => handleDeleteClicked(data)}
                        title="Delete Document"
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 dark:hover:text-red-400 rounded-lg transition-colors"
                    >
                        <BiTrash size={18} />
                    </button>
                )}
            </div>
        );
    };

    const [columnDefs] = useState([
        {
            headerName: '#',
            field: 'rowNumber',
            width: 50,
            cellRenderer: rowNumberRenderer,
            pinned: 'left',
            filter: false,
            sortable: false,
        },
        {
            headerName: 'Document Title',
            field: 'title',
            minWidth: 260,
            flex: 2,
            cellRenderer: DocumentNameRenderer,
            filter: false,
            sortable: false,
            pinned: 'left',
        },
        {
            headerName: 'Category',
            field: 'category',
            width: 150,
            cellRenderer: CategoryRenderer,
            filter: false,
            sortable: false,
            cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        },
        {
            headerName: 'Team',
            field: 'team',
            valueGetter: (params: any) =>
                params.data?.team || params.data?.teamId || '-',
            width: 160,
            filter: false,
            sortable: false,
            cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        },
        {
            headerName: 'Version',
            field: 'version',
            width: 100,
            cellRenderer: VersionRenderer,
            filter: false,
            sortable: false,
            cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        },
        {
            headerName: 'Status',
            field: 'status',
            width: 140,
            cellRenderer: StatusRenderer,
            filter: false,
            sortable: false,
            cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        },
        {
            headerName: 'Created By',
            field: 'createby_id_name',
            width: 220,
            cellRenderer: UploadedByRenderer,
            filter: false,
            sortable: false,
            cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        },
        // {
        //     headerName: 'Updated By',
        //     field: 'modifiedby_name',
        //     valueGetter: (params: any) =>
        //         params.data?.modifiedby_name || params.data?.modifiedby || '-',
        //     width: 130,
        //     sortable: true,
        //     cellStyle: () => ({
        //         display: 'flex',
        //         alignItems: 'center',
        //         fontSize: '12px',
        //     }),
        // },
        {
            headerName: 'Date',
            field: 'ts_created',
            valueFormatter: (params: any) =>
                params.value
                    ? new Date(params.value).toLocaleString('en-GB')
                    : '',
            width: 150,
            filter: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                color: '#6B7280',
            }),
        },
        {
            headerName: 'Change History',
            field: 'description',
            minWidth: 200,
            flex: 1,
            filter: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                color: '#4B5563',
            }),
        },
        {
            headerName: 'Action',
            field: 'actions',
            pinned: 'right',
            width: 250,
            suppressCellFlash: true,
            cellRenderer: ActionCellRenderer,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                justifyItems: 'center',
            }),
        },
    ]);

    return (
        <div className="main-data-grid bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden p-2 flex flex-col">
            <AgDataGridServerPaging
                ref={gridRef}
                columnDefs={columnDefs}
                getServerSideDataSource={getServerSideDataSource}
                defaultPageSize={100}
                tableDimension={{ width: '100%', height: '70vh' }}
                defaultColDef={{
                    sortable: true,
                    resizable: true,
                    minWidth: 80,
                    minHeight: '80vh',
                    wrapHeaderText: true,
                    wrapText: true,
                    autoHeight: true,
                }}
            />
        </div>
    );
};

export default CatalogTable;
